// packages/core/src/learning/schools/cashflow-assets.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. Worldview named 'paula-pant'
// (valid FigureId) — wire into `figures` during the cross-ref pass.
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const CASHFLOW_ASSETS_EXHIBIT: SchoolExhibitEntry = {
	id: 'cashflow-assets',
	tagline:
		'Cashflow assets believes ownership should be judged by the work behind the checks.',
	worldviewSummary:
		'Cashflow assets is paying attention to whether an asset can send money back to you while you still own it. This lens protects you from confusing paper wealth with usable income. It accepts that cash flow usually comes with work, risk, taxes, maintenance, vacancies, concentration, and slower growth. The blind spot is that a steady check can make a weak asset feel safer than it really is.',
	coreBeliefs: [
		'Cashflow assets believes an asset is not just something that might rise in price.',
		'Cashflow assets believes recurring income can make freedom feel more concrete.',
		'Cashflow assets believes ownership has to be judged after expenses, taxes, repairs, and time.',
		'Cashflow assets believes cash flow can reduce pressure when income from work is unstable.',
		'Cashflow assets believes boring operating details matter more than the income story.',
		'Cashflow assets believes concentration risk can hide inside assets that feel familiar.',
	],
	warnsAgainst: [
		'Confusing gross income with money you actually keep.',
		'Calling something passive before counting the work, risk, and upkeep.',
		'Buying the story of monthly income without stress testing bad months.',
		'Ignoring concentration risk because the asset feels tangible.',
	],
	helps: 'This lens helps people who want to understand income producing ownership without falling for passive income hype. It may not fit someone who wants maximum simplicity, broad diversification, or very low maintenance investing.',
	figures: [],
	canon: [
		{
			title: 'Afford Anything',
			author: 'Paula Pant',
			oneLineIdea:
				'The point is not owning more stuff, it is choosing assets and habits that buy back control.',
		},
		{
			title: 'Rich Dad Poor Dad',
			author: 'Robert T. Kiyosaki',
			oneLineIdea:
				'The useful idea is simple: assets should be judged by how they affect your cash flow, not just by how impressive they sound.',
		},
		{
			title: 'Net operating income',
			oneLineIdea:
				'A cash flowing asset only starts to get real after the ordinary costs are counted.',
		},
		{
			title: 'Cap rate thinking',
			oneLineIdea:
				'The income from an asset has to be compared against the price paid and the risks accepted.',
		},
		{
			title: 'Vacancy and repair reserves',
			oneLineIdea:
				'Cash flow is not real if one empty month or one repair wipes out the plan.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-cashflow-assets',
	debateIds: [],
	applyActions: [
		{
			label: 'Compare this asset idea against your real monthly surplus',
			action: {
				type: 'openDashboard',
				label: 'Check your monthly picture',
			},
		},
		{
			label: 'Test how extra monthly cash changes your freedom path',
			action: {
				type: 'openSimulator',
				label: 'Simulate extra monthly cash',
				scenarioType: 'custom',
			},
		},
		{
			label: 'Check how much runway would protect a slow month',
			action: {
				type: 'calculateRunway',
				label: 'Check your runway',
			},
		},
		{
			label: 'Write the real cost behind a cash flow idea',
			action: {
				type: 'noToolAction',
				label: 'List the hidden costs',
				prompt: 'Write the costs, time, repairs, taxes, and bad month risks behind one asset you are considering.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Passive income is not passive if you forgot the repairs, taxes, and bad months.',
			kind: 'hook',
		},
		{
			line: 'Cashflow assets asks one blunt question: what actually reaches your pocket?',
			kind: 'hook',
		},
		{
			line: 'What asset looked profitable until you counted the work behind it?',
			kind: 'interview-question',
		},
		{
			line: 'If you bought one cash flowing asset again, what risk would you model first?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk helps you test the income story before you marry the asset.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Most finance apps show balances. Nisk helps you see whether the cash flow changes your options.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Show an asset that sounds passive, then walk through every cost people forget.',
			kind: 'creator-prompt',
		},
		{
			line: 'Ask someone whether cash flow gave them freedom or gave them another job.',
			kind: 'creator-prompt',
		},
	],
	revision: 1,
};
